import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import { type Mesh } from 'three';
import { useNavigationStore } from '@/store/useNavigationStore';
import { palette } from '@/theme/palette';
import { getPage, PAGES } from './pages';

interface FillOpacity { fillOpacity: number }

/**
 * Destination "billboard" that materialises in front of the camera
 * once a warp arrives. Renders the active page's title and body text
 * on top of the per-highway frame mesh. The text fades in over 0.5s
 * after arrival and fades out instantly when a return is triggered.
 */
export function PageScreen() {
  const phase = useNavigationStore((s) => s.phase);
  const pageId = useNavigationStore((s) => s.page);
  const page = getPage(pageId);

  const titleRef = useRef<Mesh & FillOpacity>(null);
  const bodyRef = useRef<Mesh & FillOpacity>(null);
  const visibleAtRef = useRef<number | null>(null);

  useFrame(() => {
    const arrived = phase === 'arrived';
    if (arrived && visibleAtRef.current === null) {
      visibleAtRef.current = performance.now();
    }
    if (!arrived) {
      visibleAtRef.current = null;
    }
    const opacity = arrived
      ? Math.min(1, ((performance.now() - (visibleAtRef.current ?? 0)) / 500))
      : 0;
    if (titleRef.current) titleRef.current.fillOpacity = opacity;
    if (bodyRef.current) bodyRef.current.fillOpacity = opacity;
  });

  if (!page) return null;

  // Place the screen group at the active highway's far end, in world space
  // we render once at the highway-local position which means we need to
  // reuse the same yaw rotation as the highway itself.
  const meta = PAGES.find((p) => p.id === page.id)!;

  return (
    <group rotation={[0, meta.yaw, 0]}>
      <group position={[0, 1.6, -92]}>
        <Text
          ref={titleRef as unknown as React.RefObject<Mesh>}
          position={[0, 1.4, 0.05]}
          fontSize={0.85}
          color={palette.text}
          anchorX="center"
          anchorY="middle"
          maxWidth={9}
          textAlign="center"
          letterSpacing={-0.02}
          outlineWidth={0.006}
          outlineColor={palette.bgDeep}
          outlineOpacity={0.5}
        >
          {meta.label}
        </Text>
        <Text
          ref={bodyRef as unknown as React.RefObject<Mesh>}
          position={[0, -0.4, 0.05]}
          fontSize={0.24}
          color={palette.textSoft}
          anchorX="center"
          anchorY="middle"
          maxWidth={8}
          textAlign="center"
          lineHeight={1.5}
        >
          {meta.body}
        </Text>
      </group>
    </group>
  );
}
